import { Link, useLocation } from "react-router-dom";
import { Heart, ShoppingBag, ArrowLeft, Coins } from "lucide-react";
import { useEffect, useState } from "react";
import { getFavorites, getSessionId } from "@/lib/api";
import api from "@/lib/api";
import { formatInr } from "@/lib/productTypes";

export default function ShopHeader({ shop, slug, back = false, title }) {
    const [favCount, setFavCount] = useState(0);
    const [cartCount, setCartCount] = useState(0);
    const loc = useLocation();

    useEffect(() => {
        setFavCount(getFavorites().filter((f) => f.shop_slug === slug).length);
        const sid = getSessionId();
        api.get(`/cart/${sid}`).then(({ data }) => setCartCount(data.items?.length || 0)).catch(() => {});
    }, [slug, loc.pathname]);

    // Show gold-rate banner whenever the shop has set today's rate. (Only
    // jewellery-selling shops ever set rates, so this auto-targets them.)
    const showGoldRate = !!(shop?.gold_rate_22k || shop?.gold_rate_18k || shop?.gold_rate_silver);

    return (
        <header className="ayraa-glass sticky top-0 z-40">
            <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                    {back ? (
                        <Link
                            to={`/shop/${slug}`}
                            className="w-9 h-9 -ml-2 grid place-items-center hover:bg-brand-subtle rounded-full transition"
                            aria-label="back"
                            data-testid="back-link"
                        >
                            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                    ) : null}
                    <Link to={`/shop/${slug}`} className="min-w-0 flex flex-col leading-tight">
                        {title || shop?.location ? (
                            <span className="ayraa-overline truncate">{title || shop?.location}</span>
                        ) : null}
                        <span className="font-display text-lg sm:text-xl truncate tracking-editorial">
                            {shop?.name || "Vastra"}
                        </span>
                    </Link>
                </div>
                <div className="flex items-center gap-1">
                    <Link
                        to={`/shop/${slug}/favorites`}
                        className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-brand-subtle transition"
                        data-testid="favorites-link"
                        aria-label="Favorites"
                    >
                        <Heart className="w-4 h-4" strokeWidth={1.5} />
                        {favCount > 0 ? (
                            <span className="absolute -top-0.5 -right-0.5 bg-brand-bronze text-white font-mono text-[9px] rounded-full w-4 h-4 grid place-items-center">{favCount}</span>
                        ) : null}
                    </Link>
                    <Link
                        to={`/shop/${slug}/cart`}
                        className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-brand-subtle transition"
                        data-testid="cart-link"
                        aria-label="Cart"
                    >
                        <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                        {cartCount > 0 ? (
                            <span className="absolute -top-0.5 -right-0.5 bg-brand-bronze text-white font-mono text-[9px] rounded-full w-4 h-4 grid place-items-center">{cartCount}</span>
                        ) : null}
                    </Link>
                </div>
            </div>
            {showGoldRate ? (
                <div className="border-t border-brand-border bg-brand-paper/70" data-testid="gold-rate-banner">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2 flex items-center gap-3 sm:gap-6 text-[11px] font-mono uppercase tracking-wider-2 text-brand-muted overflow-x-auto">
                        <span className="flex items-center gap-1.5 text-brand-bronze shrink-0">
                            <Coins className="w-3 h-3" strokeWidth={1.5} />
                            <span>Today&apos;s rate</span>
                        </span>
                        {shop.gold_rate_22k ? (
                            <span className="shrink-0" data-testid="gold-rate-22k">22K · <b className="text-brand-ink not-italic">{formatInr(shop.gold_rate_22k)}/g</b></span>
                        ) : null}
                        {shop.gold_rate_18k ? (
                            <span className="shrink-0" data-testid="gold-rate-18k">18K · <b className="text-brand-ink not-italic">{formatInr(shop.gold_rate_18k)}/g</b></span>
                        ) : null}
                        {shop.gold_rate_silver ? (
                            <span className="shrink-0" data-testid="gold-rate-silver">Silver · <b className="text-brand-ink not-italic">{formatInr(shop.gold_rate_silver)}/g</b></span>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </header>
    );
}
